import { HttpCoreService } from "@/services/HttpCoreService";
import type {
  ETMFDocumentDto,
  ETMFDocumentsQuery,
  ETMFUploadPayload,
  ETMFUploadResponse,
  ETMFQCReviewItem,
  ETMFCompletenessResponse,
  ETMFRetentionPolicyDto,
  ETMFPaginatedData,
  ETMFDocumentUpdatePayload,
  ETMFQCCompletePayload,
  ETMFArtifactTypeDto,
  ETMFDocumentVersionDto,
  ETMFGovernanceContract,
  ETMFGovernedArtifact,
  ETMFTmfModelDto,
  ETMFTmfModelVersionDto,
  CreateTmfModelPayload,
  CreateTmfModelVersionPayload,
  GovernanceReasonPayload,
  ETMFGovernanceImpact,
  ETMFLegacyMappingDto,
  UpsertLegacyMappingPayload,
  ETMFChangeRequestDto,
  ETMFGovernanceCatalog,
} from "@/types/etmf/etmf.type";
import {
  ETMF_POLICY_STATUS,
  isEtmfPolicyStatus,
} from "@/types/etmfStatus.type";

export interface IETMFRepository {
  getDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>>;
  getDocument(id: string): Promise<ETMFDocumentDto | null>;
  getDocumentDownloadUrl(
    id: string,
  ): Promise<{ url: string; fileName: string }>;
  getDocumentStats(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
  }): Promise<Record<string, unknown>>;
  uploadDocument(payload: ETMFUploadPayload): Promise<ETMFUploadResponse>;
  updateDocument(
    id: string,
    payload: ETMFDocumentUpdatePayload,
  ): Promise<ETMFDocumentDto | null>;
  deleteDocument(id: string): Promise<void>;
  archiveDocument(id: string): Promise<void>;
  restoreDocument(id: string): Promise<void>;
  uploadNewVersion(
    id: string,
    file: File,
    version_notes?: string,
  ): Promise<ETMFDocumentDto | null>;
  getDocumentVersions(id: string): Promise<ETMFDocumentVersionDto[]>;
  getQCPendingReviews(query?: {
    page?: number;
    limit?: number;
    status?: string;
    study_id?: string;
    site_id?: string;
  }): Promise<ETMFPaginatedData<ETMFQCReviewItem>>;
  getQCReview(id: string): Promise<ETMFQCReviewItem | null>;
  getReviewByDocument(documentId: string): Promise<ETMFQCReviewItem | null>;
  completeQCReview(
    id: string,
    payload: ETMFQCCompletePayload,
  ): Promise<ETMFQCReviewItem | null>;
  getCompleteness(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
    group_by?: "study" | "site" | "zone" | "study-site";
    use_qc_checklist?: boolean;
  }): Promise<ETMFCompletenessResponse>;
  getRetentionPolicies(query?: {
    artifact_type?: string;
    status?: "Active" | "Expired" | "ACTIVE" | "EXPIRED";
  }): Promise<ETMFRetentionPolicyDto[]>;
  createRetentionPolicy(body: {
    artifactType: string;
    retentionPeriodYears: number;
  }): Promise<ETMFRetentionPolicyDto>;
  updateRetentionPolicy(
    id: string,
    body: { retentionPeriodYears?: number; status?: "ACTIVE" | "EXPIRED" },
  ): Promise<ETMFRetentionPolicyDto>;
  getArtifactTypes(query?: {
    zone?: string;
    required?: boolean;
  }): Promise<ETMFArtifactTypeDto[]>;
  getInspectionDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>>;
  /** Binary ZIP — long timeout for large studies. */
  exportTmSnapshot(studyId: string): Promise<Blob>;
  getGovernanceContract(query?: {
    study_id?: string;
    site_id?: string;
    country_code?: string;
  }): Promise<ETMFGovernanceContract>;
  getEligibleArtifacts(query: {
    study_id: string;
    site_id?: string;
    country_code?: string;
  }): Promise<{
    items: ETMFGovernedArtifact[];
    governed: boolean;
    model_version_id?: string;
  }>;
  getGovernanceCatalog(query?: {
    model_id?: string;
    version_id?: string;
  }): Promise<ETMFGovernanceCatalog>;
  listGovernanceModels(): Promise<{ items: ETMFTmfModelDto[] }>;
  createGovernanceModel(body: CreateTmfModelPayload): Promise<ETMFTmfModelDto>;
  listGovernanceVersions(
    modelId: string,
  ): Promise<{ items: ETMFTmfModelVersionDto[] }>;
  createGovernanceVersion(
    modelId: string,
    body: CreateTmfModelVersionPayload,
  ): Promise<ETMFTmfModelVersionDto>;
  submitGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto>;
  approveGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto>;
  previewGovernanceImpact(versionId: string): Promise<ETMFGovernanceImpact>;
  assignStudyModel(
    studyId: string,
    body: { model_version_id: string; reason: string; row_version?: number },
  ): Promise<Record<string, unknown>>;
  listLegacyMappings(query?: {
    study_id?: string;
  }): Promise<{ items: ETMFLegacyMappingDto[] }>;
  upsertLegacyMapping(
    body: UpsertLegacyMappingPayload,
  ): Promise<ETMFLegacyMappingDto>;
  listChangeRequests(query: {
    study_id: string;
  }): Promise<{ items: ETMFChangeRequestDto[] }>;
  createChangeRequest(body: {
    study_id: string;
    reason: string;
  }): Promise<ETMFChangeRequestDto>;
}

export class ETMFRepository implements IETMFRepository {
  constructor(private readonly httpService: HttpCoreService) {}

  private buildQuery(params?: Record<string, unknown>): string {
    if (!params) {
      return "";
    }

    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        search.append(key, String(value));
      }
    });

    const query = search.toString();
    return query ? `?${query}` : "";
  }

  private normalizePaginated<T>(input: unknown): ETMFPaginatedData<T> {
    const fallback: ETMFPaginatedData<T> = {
      items: [],
      total: 0,
      page: 1,
      limit: 20,
    };

    if (!input || typeof input !== "object") {
      return fallback;
    }

    const data = input as Record<string, unknown>;

    if (Array.isArray(data.items)) {
      return {
        items: data.items as T[],
        total: typeof data.total === "number" ? data.total : data.items.length,
        page: typeof data.page === "number" ? data.page : 1,
        limit: typeof data.limit === "number" ? data.limit : 20,
      };
    }

    if (data.data && typeof data.data === "object") {
      const nested = data.data as Record<string, unknown>;
      if (Array.isArray(nested.items)) {
        return {
          items: nested.items as T[],
          total:
            typeof nested.total === "number"
              ? nested.total
              : nested.items.length,
          page: typeof nested.page === "number" ? nested.page : 1,
          limit: typeof nested.limit === "number" ? nested.limit : 20,
        };
      }
    }

    if (Array.isArray(input)) {
      return {
        items: input as T[],
        total: input.length,
        page: 1,
        limit: input.length || 20,
      };
    }

    return fallback;
  }

  private normalizeArray<T>(input: unknown): T[] {
    if (Array.isArray(input)) {
      return input as T[];
    }
    if (input && typeof input === "object") {
      const data = input as Record<string, unknown>;
      if (Array.isArray(data.items)) {
        return data.items as T[];
      }
      if (
        data.data &&
        typeof data.data === "object" &&
        Array.isArray((data.data as Record<string, unknown>).items)
      ) {
        return (data.data as Record<string, unknown>).items as T[];
      }
    }
    return [];
  }

  async getDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>> {
    const queryWithDefaults: ETMFDocumentsQuery = {
      page: query?.page ?? 1,
      limit: query?.limit ?? 50,
      ...query,
    };

    const response = await this.httpService.get(
      `/etmf/documents${this.buildQuery(queryWithDefaults as Record<string, unknown>)}`,
    );
    return this.normalizePaginated<ETMFDocumentDto>(response.data);
  }

  async getDocument(id: string): Promise<ETMFDocumentDto | null> {
    const response = await this.httpService.get<ETMFDocumentDto>(
      `/etmf/documents/${encodeURIComponent(id)}`,
    );
    return response.data ?? null;
  }

  async getDocumentDownloadUrl(
    id: string,
  ): Promise<{ url: string; fileName: string }> {
    const response = await this.httpService.get<{
      url: string;
      fileName: string;
    }>(`/etmf/documents/${encodeURIComponent(id)}/download-url`);
    const data = response.data;
    if (!data?.url) {
      throw new Error("Download URL not returned");
    }
    return { url: data.url, fileName: data.fileName ?? "document" };
  }

  async getDocumentStats(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.httpService.get(
      `/etmf/documents/stats${this.buildQuery(query as Record<string, unknown>)}`,
    );
    if (response.data && typeof response.data === "object") {
      return response.data as Record<string, unknown>;
    }
    return {};
  }

  async uploadDocument(
    payload: ETMFUploadPayload,
  ): Promise<ETMFUploadResponse> {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("study_id", payload.study_id);
    if (payload.site_id) {
      formData.append("site_id", payload.site_id);
    }
    formData.append("zone", payload.zone);
    formData.append("artifact_type", payload.artifact_type);
    if (payload.artifact_code) {
      formData.append("artifact_code", payload.artifact_code);
    }
    if (payload.version) {
      formData.append("version", payload.version);
    }
    if (payload.version_notes) {
      formData.append("version_notes", payload.version_notes);
    }
    if (payload.expiration_date) {
      formData.append("expiration_date", payload.expiration_date);
    }
    if (payload.is_blinded !== undefined) {
      formData.append("is_blinded", String(payload.is_blinded));
    }
    if (payload.submit_for_review !== undefined) {
      formData.append("submit_for_review", String(payload.submit_for_review));
    }
    if (payload.country_id) {
      formData.append("country_id", payload.country_id);
    }
    if (payload.region) {
      formData.append("region", payload.region);
    }
    if (payload.artifact_definition_id) {
      formData.append("artifact_definition_id", payload.artifact_definition_id);
    }
    if (payload.model_version_id) {
      formData.append("model_version_id", payload.model_version_id);
    }

    const response = await this.httpService.postForm<ETMFUploadResponse>(
      "/etmf/documents/upload",
      formData,
    );
    return response.data ?? { id: "unknown" };
  }

  async updateDocument(
    id: string,
    payload: ETMFDocumentUpdatePayload,
  ): Promise<ETMFDocumentDto | null> {
    const response = await this.httpService.put<ETMFDocumentDto>(
      `/etmf/documents/${encodeURIComponent(id)}`,
      payload,
    );
    return response.data ?? null;
  }

  async deleteDocument(id: string): Promise<void> {
    await this.httpService.delete(`/etmf/documents/${encodeURIComponent(id)}`);
  }

  async archiveDocument(id: string): Promise<void> {
    await this.httpService.post(
      `/etmf/documents/${encodeURIComponent(id)}/archive`,
      {},
    );
  }

  async restoreDocument(id: string): Promise<void> {
    await this.httpService.post(
      `/etmf/documents/${encodeURIComponent(id)}/restore`,
      {},
    );
  }

  async uploadNewVersion(
    id: string,
    file: File,
    version_notes?: string,
  ): Promise<ETMFDocumentDto | null> {
    const formData = new FormData();
    formData.append("file", file);
    if (version_notes) {
      formData.append("version_notes", version_notes);
    }
    const response = await this.httpService.postForm<ETMFDocumentDto>(
      `/etmf/documents/${encodeURIComponent(id)}/new-version`,
      formData,
    );
    return response.data ?? null;
  }

  async getDocumentVersions(id: string): Promise<ETMFDocumentVersionDto[]> {
    const response = await this.httpService.get<ETMFDocumentVersionDto[]>(
      `/etmf/documents/${encodeURIComponent(id)}/versions`,
    );
    return this.normalizeArray<ETMFDocumentVersionDto>(response.data);
  }

  async getQCPendingReviews(query?: {
    page?: number;
    limit?: number;
    status?: string;
    study_id?: string;
    site_id?: string;
  }): Promise<ETMFPaginatedData<ETMFQCReviewItem>> {
    const response = await this.httpService.get(
      `/etmf/qc-review/pending${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return this.normalizePaginated<ETMFQCReviewItem>(response.data);
  }

  async getQCReview(id: string): Promise<ETMFQCReviewItem | null> {
    const response = await this.httpService.get<ETMFQCReviewItem>(
      `/etmf/qc-review/${encodeURIComponent(id)}`,
    );
    return response.data ?? null;
  }

  /** Get or create QC review for a document (for PENDING_REVIEW docs). */
  async getReviewByDocument(
    documentId: string,
  ): Promise<ETMFQCReviewItem | null> {
    const response = await this.httpService.get<ETMFQCReviewItem>(
      `/etmf/qc-review/by-document/${encodeURIComponent(documentId)}`,
    );
    return response.data ?? null;
  }

  async completeQCReview(
    id: string,
    payload: ETMFQCCompletePayload,
  ): Promise<ETMFQCReviewItem | null> {
    const response = await this.httpService.put<ETMFQCReviewItem>(
      `/etmf/qc-review/${encodeURIComponent(id)}/complete`,
      payload,
    );
    return response.data ?? null;
  }

  async getCompleteness(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
    group_by?: "study" | "site" | "zone" | "study-site";
    use_qc_checklist?: boolean;
  }): Promise<ETMFCompletenessResponse> {
    const response = await this.httpService.get(
      `/etmf/completeness${this.buildQuery(query as Record<string, unknown>)}`,
    );

    if (response.data && typeof response.data === "object") {
      const data = response.data as Record<string, unknown>;

      if (Array.isArray(data.items)) {
        return {
          items: data.items as ETMFCompletenessResponse["items"],
          summary: data.summary as ETMFCompletenessResponse["summary"],
          ...pickCompletenessGovernance(data),
        };
      }

      if (
        data.data &&
        typeof data.data === "object" &&
        Array.isArray((data.data as Record<string, unknown>).items)
      ) {
        const nested = data.data as Record<string, unknown>;
        return {
          items: nested.items as ETMFCompletenessResponse["items"],
          summary: nested.summary as ETMFCompletenessResponse["summary"],
          ...pickCompletenessGovernance(nested),
        };
      }
    }

    return { items: [] };
  }

  async getRetentionPolicies(query?: {
    artifact_type?: string;
    status?: "Active" | "Expired" | "ACTIVE" | "EXPIRED";
  }): Promise<ETMFRetentionPolicyDto[]> {
    const params = { ...query } as Record<string, unknown>;
    if (
      typeof params.status === "string" &&
      isEtmfPolicyStatus(params.status, ETMF_POLICY_STATUS.ACTIVE)
    ) {
      params.status = "ACTIVE";
    }
    if (
      typeof params.status === "string" &&
      isEtmfPolicyStatus(params.status, ETMF_POLICY_STATUS.EXPIRED)
    ) {
      params.status = "EXPIRED";
    }
    const response = await this.httpService.get(
      `/etmf/retention-policies${this.buildQuery(params)}`,
    );
    return this.normalizeArray<ETMFRetentionPolicyDto>(response.data);
  }

  async createRetentionPolicy(body: {
    artifactType: string;
    retentionPeriodYears: number;
  }): Promise<ETMFRetentionPolicyDto> {
    const response = await this.httpService.post<ETMFRetentionPolicyDto>(
      "/etmf/retention-policies",
      body,
    );
    return response.data ?? {};
  }

  async updateRetentionPolicy(
    id: string,
    body: {
      retentionPeriodYears?: number;
      status?: "ACTIVE" | "EXPIRED";
    },
  ): Promise<ETMFRetentionPolicyDto> {
    const response = await this.httpService.put<ETMFRetentionPolicyDto>(
      `/etmf/retention-policies/${id}`,
      body,
    );
    return response.data ?? {};
  }

  async getArtifactTypes(query?: {
    zone?: string;
    required?: boolean;
  }): Promise<ETMFArtifactTypeDto[]> {
    const response = await this.httpService.get(
      `/etmf/artifact-types${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return this.normalizeArray<ETMFArtifactTypeDto>(response.data);
  }

  async getInspectionDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>> {
    const response = await this.httpService.get(
      `/etmf/inspection-mode/documents${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return this.normalizePaginated<ETMFDocumentDto>(response.data);
  }

  async exportTmSnapshot(studyId: string): Promise<Blob> {
    return this.httpService.getBlob(
      `/etmf/inspection-mode/export-snapshot${this.buildQuery({ study_id: studyId })}`,
      { timeout: 300_000 },
    );
  }

  async getGovernanceContract(query?: {
    study_id?: string;
    site_id?: string;
    country_code?: string;
  }): Promise<ETMFGovernanceContract> {
    const response = await this.httpService.get<ETMFGovernanceContract>(
      `/etmf/governance/contract${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return (
      response.data ?? {
        governed: false,
        reason: "UNAVAILABLE",
        activeModel: null,
      }
    );
  }

  async getEligibleArtifacts(query: {
    study_id: string;
    site_id?: string;
    country_code?: string;
  }): Promise<{
    items: ETMFGovernedArtifact[];
    governed: boolean;
    model_version_id?: string;
  }> {
    const response = await this.httpService.get<{
      items: ETMFGovernedArtifact[];
      governed: boolean;
      model_version_id?: string;
    }>(
      `/etmf/governance/eligible-artifacts${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return response.data ?? { items: [], governed: false };
  }

  async getGovernanceCatalog(query?: {
    model_id?: string;
    version_id?: string;
  }): Promise<ETMFGovernanceCatalog> {
    const response = await this.httpService.get<ETMFGovernanceCatalog>(
      `/etmf/governance/catalog${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return (
      response.data ?? {
        source: "artifact_types",
        zones: [],
        artifacts: [],
      }
    );
  }

  async listGovernanceModels(): Promise<{ items: ETMFTmfModelDto[] }> {
    const response = await this.httpService.get<{
      items?: Array<Record<string, unknown>>;
    }>("/etmf/governance/models");
    return {
      items: (response.data?.items ?? []).map((row) =>
        this.toGovernanceModel(row),
      ),
    };
  }

  async createGovernanceModel(
    body: CreateTmfModelPayload,
  ): Promise<ETMFTmfModelDto> {
    const response = await this.httpService.post<Record<string, unknown>>(
      "/etmf/governance/models",
      body,
    );
    return this.toGovernanceModel(response.data ?? {});
  }

  async listGovernanceVersions(
    modelId: string,
  ): Promise<{ items: ETMFTmfModelVersionDto[] }> {
    const response = await this.httpService.get<{
      items?: Array<Record<string, unknown>>;
    }>(`/etmf/governance/models/${encodeURIComponent(modelId)}/versions`);
    return {
      items: (response.data?.items ?? []).map((row) =>
        this.toGovernanceVersion(row),
      ),
    };
  }

  async createGovernanceVersion(
    modelId: string,
    body: CreateTmfModelVersionPayload,
  ): Promise<ETMFTmfModelVersionDto> {
    const response = await this.httpService.post<Record<string, unknown>>(
      `/etmf/governance/models/${encodeURIComponent(modelId)}/versions`,
      body,
    );
    return this.toGovernanceVersion(response.data ?? {});
  }

  async submitGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto> {
    const response = await this.httpService.post<Record<string, unknown>>(
      `/etmf/governance/model-versions/${encodeURIComponent(versionId)}/submit`,
      body,
    );
    return this.toGovernanceVersion(response.data ?? {});
  }

  async approveGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto> {
    const response = await this.httpService.post<Record<string, unknown>>(
      `/etmf/governance/model-versions/${encodeURIComponent(versionId)}/approve`,
      body,
    );
    return this.toGovernanceVersion(response.data ?? {});
  }

  async previewGovernanceImpact(
    versionId: string,
  ): Promise<ETMFGovernanceImpact> {
    const response = await this.httpService.post<Record<string, unknown>>(
      `/etmf/governance/model-versions/${encodeURIComponent(versionId)}/preview-impact`,
      {},
    );
    const row = response.data ?? {};
    return {
      version_id: pickGovernanceString(row, "version_id", "versionId"),
      status: pickGovernanceString(row, "status"),
      artifact_count: pickGovernanceNumber(
        row,
        "artifact_count",
        "artifactCount",
      ),
      assigned_study_count: pickGovernanceNumber(
        row,
        "assigned_study_count",
        "assignedStudyCount",
      ),
    };
  }

  async assignStudyModel(
    studyId: string,
    body: { model_version_id: string; reason: string; row_version?: number },
  ): Promise<Record<string, unknown>> {
    const response = await this.httpService.put<Record<string, unknown>>(
      `/etmf/governance/studies/${encodeURIComponent(studyId)}/model-assignment`,
      body,
    );
    return response.data ?? {};
  }

  async listLegacyMappings(query?: {
    study_id?: string;
  }): Promise<{ items: ETMFLegacyMappingDto[] }> {
    const response = await this.httpService.get<{
      items?: ETMFLegacyMappingDto[];
    }>(
      `/etmf/governance/legacy-mappings${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return { items: response.data?.items ?? [] };
  }

  async upsertLegacyMapping(
    body: UpsertLegacyMappingPayload,
  ): Promise<ETMFLegacyMappingDto> {
    const response = await this.httpService.put<ETMFLegacyMappingDto>(
      "/etmf/governance/legacy-mappings",
      body,
    );
    return response.data ?? ({} as ETMFLegacyMappingDto);
  }

  async listChangeRequests(query: {
    study_id: string;
  }): Promise<{ items: ETMFChangeRequestDto[] }> {
    const response = await this.httpService.get<{
      items?: ETMFChangeRequestDto[];
    }>(
      `/etmf/governance/change-requests${this.buildQuery(query as Record<string, unknown>)}`,
    );
    return { items: response.data?.items ?? [] };
  }

  async createChangeRequest(body: {
    study_id: string;
    reason: string;
  }): Promise<ETMFChangeRequestDto> {
    const response = await this.httpService.post<ETMFChangeRequestDto>(
      "/etmf/governance/change-requests",
      body,
    );
    return response.data ?? ({} as ETMFChangeRequestDto);
  }

  private toGovernanceModel(row: Record<string, unknown>): ETMFTmfModelDto {
    return {
      id: pickGovernanceString(row, "id"),
      name: pickGovernanceString(row, "name"),
      modelCode: pickGovernanceString(row, "modelCode", "model_code"),
      ownerType: pickGovernanceString(row, "ownerType", "owner_type"),
      namespace: pickGovernanceString(row, "namespace") || undefined,
    };
  }

  private toGovernanceVersion(
    row: Record<string, unknown>,
  ): ETMFTmfModelVersionDto {
    return {
      id: pickGovernanceString(row, "id"),
      modelId: pickGovernanceString(row, "modelId", "model_id"),
      versionLabel: pickGovernanceString(row, "versionLabel", "version_label"),
      status: pickGovernanceString(row, "status"),
      authoredBy:
        pickGovernanceString(row, "authoredBy", "authored_by") || undefined,
      submittedBy:
        pickGovernanceString(row, "submittedBy", "submitted_by") || null,
      approvedBy:
        pickGovernanceString(row, "approvedBy", "approved_by") || null,
      rowVersion: pickGovernanceNumber(row, "rowVersion", "row_version") || 1,
      changeReason:
        pickGovernanceString(row, "changeReason", "change_reason") || null,
    };
  }
}

function pickCompletenessGovernance(
  source: Record<string, unknown>,
): Pick<
  ETMFCompletenessResponse,
  "governed" | "mode" | "states" | "expectations"
> {
  const mode =
    source.mode === "governed" || source.mode === "legacy-file-count"
      ? source.mode
      : undefined;
  return {
    governed:
      typeof source.governed === "boolean" ? source.governed : undefined,
    mode,
    states:
      source.states && typeof source.states === "object"
        ? (source.states as Record<string, number>)
        : undefined,
    expectations: Array.isArray(source.expectations)
      ? (source.expectations as ETMFCompletenessResponse["expectations"])
      : undefined,
  };
}

function pickGovernanceString(
  row: Record<string, unknown>,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return "";
}

function pickGovernanceNumber(
  row: Record<string, unknown>,
  ...keys: string[]
): number {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
}
